import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import {
  KANATI_RESPONSE_VISIBILITY,
  KANATI_RESPONSE_VISIBILITY_LABELS,
  type KanatiResponseVisibility
} from './MessageInput.constants';

/**
 * "Send to Kanati" switch with a response-visibility selector.
 * Must be rendered inside the MessageInput form provider.
 */
const KanatiToggle = ({
  clearAttachments = true
}: {
  // Kanati responses cannot carry attachments, except on Kanati service tickets
  clearAttachments?: boolean;
}) => {
  const { control, setValue } = useFormContext();
  const isInternalMessage = useWatch({ control, name: 'internal' });

  const handleToggle = (
    checked: boolean,
    onChange: (value: KanatiResponseVisibility | null) => void
  ) => {
    onChange(
      checked
        ? isInternalMessage
          ? KANATI_RESPONSE_VISIBILITY.INTERNAL
          : KANATI_RESPONSE_VISIBILITY.PUBLIC
        : null
    );
    if (checked && clearAttachments) {
      setValue('attachments', []);
    }
  };

  return (
    <Controller
      control={control}
      name="kanati_response_visibility"
      render={({ field }) => (
        <Box>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={field.value !== null}
                onChange={(event) =>
                  handleToggle(event.target.checked, field.onChange)
                }
              />
            }
            label="Send to Kanati"
            style={{ marginRight: 15, whiteSpace: 'nowrap' }}
          />
          {field.value !== null && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                renderValue={(value: KanatiResponseVisibility) =>
                  KANATI_RESPONSE_VISIBILITY_LABELS[value].label
                }
                MenuProps={{
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' }
                }}
              >
                {Object.values(KANATI_RESPONSE_VISIBILITY).map((visibility) => {
                  const { label, helpText } =
                    KANATI_RESPONSE_VISIBILITY_LABELS[visibility];

                  return (
                    <MenuItem
                      key={visibility}
                      value={visibility}
                      disabled={
                        visibility === KANATI_RESPONSE_VISIBILITY.INTERNAL &&
                        !isInternalMessage
                      }
                    >
                      <ListItemText
                        primary={label}
                        secondary={helpText}
                        slotProps={{
                          secondary: {
                            variant: 'caption',
                            color: 'text.secondary'
                          }
                        }}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        </Box>
      )}
    />
  );
};

export default KanatiToggle;
